import {NumericFormat} from "react-number-format";
import {Ided} from "../util/arutil";
import {Surcharge, SurchargePatch} from "../rpc/webrpc-client.gen.ts";
import {EditorMode} from "../forms.tsx";
import {useBomb} from "../bomb/bomb.tsx";
import {useTranslation} from "react-i18next";
import {Box, Button, FormControl, FormLabel, Input as JoyInput, Textarea, useTheme} from "@mui/joy";
import {usePatchSurchargeMutation, useUnitArmap} from "../tq/tqx.tsx";
import {useCallback} from "react";
import {useForm} from "@tanstack/react-form";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowsSpin} from "@fortawesome/pro-regular-svg-icons";

type SurchargeFormValue = Ided & SurchargePatch

export interface SurchargeEditorProps {
    surcharge: Surcharge;
    mode: EditorMode;
    afterPatch?: () => void;
}

export function SurchargeEditor({surcharge, mode, afterPatch}: SurchargeEditorProps) {
    const fvdflt: SurchargeFormValue = surcharge;
    const flavorName = surcharge.flavor.displayName;
    const ro = mode !== "rw";
    const bomb = useBomb();
    const {t} = useTranslation();
    const theme = useTheme();
    const {mutateAsync: performSubmit} = usePatchSurchargeMutation();
    const handleSubmit = useCallback(({value}: { value: SurchargeFormValue }) => {
        return performSubmit({subjectClassname: surcharge.subjectClass, surchargeId: value.id, patch: value})
            .then((rv) => {
                if (afterPatch) {
                    afterPatch();
                }
                return rv;
            });
    }, [afterPatch, performSubmit, surcharge.subjectClass]);

    const form = useForm({
        defaultValues: fvdflt,
        validators: {
            onSubmitAsync: handleSubmit,
        }
    });

    const unitid = surcharge.flavor.unitId;
    const {data: units} = useUnitArmap();
    const unitName = units?.byId.get(unitid)?.displayName;

    const headerSx = {
        gridColumnStart: "0", gridColumnEnd: "3", justifySelf: "center",
    };
    const labelSx = {
        gridColumn: 1, justifySelf: "end", alignSelf: "center",
    };
    const fieldSx = {
        gridColumn: 2, justifySelf: "center", alignSelf: "center",
    };
    const unitSx = {
        gridColumn: 3, justifySelf: "start", alignSelf: "center",
    };

    return <form
        onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit().catch(bomb);
        }}>
        <Box sx={{display: "grid", gridTemplateColumns: "1fr auto 1fr", rowGap: "1rem", mx: 4}}>
            <Box sx={{fontSize: theme.fontSize.xl, ...headerSx}}>
                {flavorName}
            </Box>
            <form.Field
                name="quantity"
                children={(field) =>
                    <FormControl orientation="horizontal" sx={{alignItems: "center", display: "contents"}}>
                        <FormLabel sx={labelSx}>{t("quantity")}</FormLabel>
                        <NumericFormat
                            value={field.state.value}
                            onValueChange={(values) => {
                                field.handleChange(Math.round((values.floatValue ?? 0)));
                            }}
                            customInput={JoyInput}
                            slotProps={{input: {type: "number", style: {textAlign: "right"}}}}
                            sx={{
                                ...fieldSx,
                                maxWidth: "8rem",
                                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                                    display: "none"
                                },
                                "& input[type=number]": {
                                    MozAppearance: "textfield"
                                }
                            }}
                            valueIsNumericString={true}
                            disabled={ro}
                        />
                        <Box sx={{ml: 1, ...unitSx}}>{unitName}</Box>
                    </FormControl>}/>
            <form.Field
                name="cost"
                children={(field) =>
                    <FormControl orientation="horizontal" sx={{display: "contents"}}>
                        <FormLabel sx={labelSx}>{t("cost")}</FormLabel>
                        <NumericFormat
                            value={field.state.value}
                            onValueChange={(values) => {
                                field.handleChange(Math.round((values.floatValue ?? 0)));
                            }}
                            customInput={JoyInput}
                            slotProps={{input: {type: "number", style: {textAlign: "right"}}}}
                            sx={{
                                ...fieldSx,
                                maxWidth: "8rem",
                                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                                    display: "none"
                                },
                                "& input[type=number]": {
                                    MozAppearance: "textfield"
                                }
                            }}
                            valueIsNumericString={true}
                            disabled={ro}
                        />
                        <Box sx={{ml: 1, ...unitSx}}>{t("costUnit")}</Box>
                    </FormControl>
                }/>

            <form.Field
                name="comment"
                children={(field) =>
                    <FormControl orientation="horizontal" sx={{display: "contents"}}>
                        <FormLabel sx={labelSx}>{t("cost")}</FormLabel>
                        <Textarea
                            value={field.state.value}
                            onChange={(evt) => {
                                field.handleChange(evt.target.value)
                            }}
                            disabled={ro}
                        />
                    </FormControl>
                }/>

            {mode == "rw"
                ? <Box sx={headerSx}>
                    <form.Subscribe
                        selector={(state) => [state.canSubmit, state.isSubmitting, state.isDirty]}
                        children={([canSubmit, isSubmitting, isDirty]) => (
                            <Button type="submit" disabled={!canSubmit || !isDirty}
                                    sx={{
                                        py: 1, px: 8, mt: 1.8, mb: 3.2,
                                        fontSize: theme.fontSize.xl
                                    }}>
                                {isSubmitting
                                    ? <FontAwesomeIcon icon={faArrowsSpin} size="2x" spin={true}
                                                       style={{color: "white", mixBlendMode: "difference"}}/>
                                    : t("sysconf.submit")}
                            </Button>
                        )}
                    />
                </Box>
                : null}
        </Box>
    </form>
}

